import React, { useCallback, useState } from "react";
import { FiUpload, FiCheckCircle, FiClock } from "react-icons/fi";
import DragDropUploader from "../common/DragDropUploader";
import Toast from "../common/Toast";

const FileOnlyDepartmentSection = ({
    quote,
    label,
    icon,
    color,
    clearFiles,
    isSubmitting,
    disabled,
    onFilesChange,
    onComplete,
    existingFiles = [],
    onSeeFile,
    onDownloadFile,
}) => {
    const [note, setNote] = useState("");

    const handleFilesChange = useCallback((files) => {
        onFilesChange(quote.quote_type, files);
    }, [onFilesChange, quote.quote_type]);

    const isCompleted = quote.department_file_status === "completed" || existingFiles.length > 0;

    return (
        <div className={`rounded-xl border ${color.border} overflow-hidden bg-white`}>
            <div className={`flex items-center justify-between px-4 py-3 ${color.bg} border-b ${color.border}`}>
                <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg ${color.iconBg} text-white flex items-center justify-center`}>
                        {icon}
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-gray-800">{label}</h3>
                        <p className="text-xs text-gray-500">
                            File-only department · No design or estimation
                        </p>
                    </div>
                </div>

                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border ${isCompleted
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-amber-100 text-amber-700 border-amber-200"
                    }`}>
                    {isCompleted ? <FiCheckCircle className="w-3 h-3" /> : <FiClock className="w-3 h-3" />}
                    {isCompleted ? "Files Completed" : "Waiting Files"}
                </span>
            </div>

            <div className="p-4 space-y-4">
                <>
                    <>
                        {existingFiles.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-xs font-semibold text-gray-500">
                                    Existing Files ({existingFiles.length})
                                </p>

                                {existingFiles.map(file => (
                                    <div
                                        key={file.id}
                                        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                                    >
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 truncate">
                                                {file.original_name || file.filename}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {file.uploaded_by_name || "Unknown"} · {file.file_type}
                                            </p>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => onSeeFile?.(file, existingFiles)}
                                                className="text-xs font-semibold text-blue-600 hover:underline"
                                            >
                                                View
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => onDownloadFile?.(file)}
                                                className="text-xs font-semibold text-gray-600 hover:underline"
                                            >
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <DragDropUploader
                            onFilesChange={handleFilesChange}
                            clearFiles={clearFiles}
                        />

                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder={`Optional note for ${label} files...`}
                        />

                        <button
                            type="button"
                            disabled={disabled || isSubmitting}
                            onClick={() => onComplete(quote, note)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FiUpload className="w-4 h-4" />
                            {isSubmitting ? "Completing..." : `Complete ${label}`}
                        </button>
                    </>
                </>

                {isCompleted && (
                    <div className="text-sm text-gray-600">
                        Completed 
                        <span className="font-semibold">
                            {quote?.department_files_completed_by_name ? ` by ${quote?.department_files_completed_by_name}` : ` on job creation`}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FileOnlyDepartmentSection;